"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useAddresses = useAddresses;
var _auth = require("@ori/auth");
var _checkoutUtils = require("@ori/checkout-utils");
var _configurationManager = require("@ori/configuration-manager");
var _environment = require("@ori/environment");
var _i18n = require("@ori/i18n");
var _logger = require("@ori/logger");
var _presentationHooks = require("@ori/presentation-hooks");
var _shoppingContext = require("@ori/shopping-context");
var _react = require("react");
var _constants = require("../../constants");
var _useAnalytics = require("../../hooks/useAnalytics");
var _useTranslations = require("../../hooks/useTranslations");
var _logger2 = require("../../logger");
var _models = require("../../models");
var _utils = require("../../utils");
var _scrollToElement = require("../../utils/scrollToElement");
var _checkoutDeliveryContext = require("../checkoutDeliveryContext");
function useAddresses({
  addressFilters,
  mutateDeliveryOptions,
  onDeliveryItemActivation,
  setActiveAddressFilter,
  selectedItemId,
  setIsDisplayingAddresses,
  setIsSelectedAddressEdited
}) {
  const logger = (0, _logger.useLogger)('useAddresses');
  const {
    formatMessage
  } = (0, _useTranslations.useTranslations)();
  const isMobile = (0, _presentationHooks.useIsMobile)();
  const addressSectionRef = (0, _react.useRef)(null);
  const [isAddressFormOpen, setIsAddressFormOpen] = (0, _react.useState)(false);
  const [isConfirmDeleteAddressDialogOpen, setIsConfirmDeleteAddressDialogOpen] = (0, _react.useState)(false);
  const [isLastAddressValid, setIsLastAddressValid] = (0, _react.useState)(false);
  const [isWaitingForNewAddressValidity, setIsWaitingForNewAddressValidity] = (0, _react.useState)(false);
  const lastAddress = (0, _react.useRef)(undefined);
  const [lastAddressId, setLastAddressId] = (0, _react.useState)(undefined);
  const [prioritizedAddressId, setPrioritizedAddressId] = (0, _react.useState)(undefined);
  const [wasLastAddressFormActionCancelled, setWasLastAddressFormActionCancelled] = (0, _react.useState)(false);
  const {
    shoppingContext: {
      customerId = _auth.NOT_LOGGED_IN_USER_ID,
      registrationId
    } = {}
  } = (0, _shoppingContext.useShoppingContext)();
  const {
    tenant
  } = (0, _environment.useEnvironment)();
  const {
    language
  } = (0, _i18n.useLanguage)();
  const {
    globalApiUrl,
    identityUrl,
    staticApiUrl
  } = (0, _configurationManager.useServiceUrls)(['globalApi', 'identity', 'staticApi']);
  const {
    handleBillingAddressRefresh,
    isFlatSelection
  } = (0, _checkoutDeliveryContext.useCheckoutDeliveryContext)();
  const {
    isAddingNewDelivery
  } = (0, _useAnalytics.useAnalytics)();
  const {
    formattedToken,
    token
  } = (0, _auth.useToken)();
  const toggleConfirmDeleteAddressDialog = (0, _react.useCallback)(() => {
    setIsConfirmDeleteAddressDialogOpen(prevState => !prevState);
  }, []);

  // LastAddressId is used only for this query, not for suggestions
  // Suggestions using prioritizedAddressId to avoid multiple requests
  function onCreateAddress() {
    (0, _logger2.logDebug)('onCreateAddress() called.');
    // analytics
    (0, _utils.dispatchUserActionEvent)([_models.MetaHierarchy.DeliverySelection, isAddingNewDelivery.current ? _models.MetaHierarchy.FirstSelection : _models.MetaHierarchy.EditSelection, _models.MetaHierarchy.New, _models.MetaHierarchy.NewAddress]);
    setLastAddressId(undefined);
    setWasLastAddressFormActionCancelled(false);
    setIsAddressFormOpen(true);
    // scroll to top of the shipping address section for flat selection UI
    if (isFlatSelection) {
      (0, _scrollToElement.scrollToElement)(addressSectionRef, isMobile);
    }
  }
  function onDeleteAddress(addressId) {
    (0, _logger2.logDebug)('onDeleteAddress() called. addressId:', addressId);
    setLastAddressId(addressId);
    setWasLastAddressFormActionCancelled(false);
    toggleConfirmDeleteAddressDialog();
  }
  function onEditAddress(addressId) {
    (0, _logger2.logDebug)('onEditAddress() called. addressId:', addressId);
    setLastAddressId(addressId);
    setWasLastAddressFormActionCancelled(false);
    setIsAddressFormOpen(true);
    // scroll to top of the shipping address section for flat selection UI
    if (isFlatSelection) {
      (0, _scrollToElement.scrollToElement)(addressSectionRef, isMobile);
    }
  }

  /**
   * Solves successful create and edit address actions.
   *
   * Basically 3 states of user address data vs. shopping session are possible:
   *
   * 1) Anonymous user w/o the pending registration session:
   *    customerId === -1,
   *    registrationId === undefined,
   *    useAnonymousCustomer === true in address-form props (thus using another set of APIs internally)
   *
   * 2) Pending registration session:
   *    customerId !== -1 (created & returned by address-form's new address action),
   *    registrationId !== undefined (created & returned by address-form's new address action),
   *    useAnonymousCustomer === true in address-form props (thus using another set of APIs internally)
   *
   * 3) Logged-in user session:
   *    customerId !== -1,
   *    registrationId === undefined,
   *    useAnonymousCustomer === false,
   *
   * State change from 1) to 2) is done via creating a new address in address-form
   * and sending the resulting addressId + registrationId through update-delivery event
   * to basket provider. This should create a new shopping session and return it back
   * as basket-updated event / shopping context data. That event gets caught & customerId + registrationId
   * will get propagated into delivery step's configuration context.
   *
   * So in this case we should call the update delivery action without specifying deliveryChannelId
   * and before checking the new address validity (as it is not possible with the anonymous session anyway).
   * The channel ID is resolved on server and returned back in the suggestions list query, as usual.
   */
  async function setAddressFormResults(newAddressId, newRegistrationId) {
    const isNewAddress = lastAddressId === undefined;
    (0, _logger2.logDebug)('setAddressFormResults() called.', '\n\tAction:', isNewAddress ? 'new address' : 'update address', '\n\taddressId:', newAddressId, '\n\tregistrationId:', newRegistrationId);
    setIsAddressFormOpen(false);
    setLastAddressId(newAddressId);
    // TODO -- maybe place this to the onNewAddressCreationSuccess() callback?
    if (isNewAddress) {
      // The new address should be auto-selected.
      // For now, we use the suggestions data to verify the validity, and, if valid, select
      // the last address in useEffect() then.
      setIsWaitingForNewAddressValidity(true);
      setIsDisplayingAddresses(true);

      // call the event triggering update-delivery when registration ID is set
      // triggers process of binding the address & registration ID with a new shopping session
      // result will come as basket-updated event & trigger refreshing of component states
      // based on configuration context from top
      if (newRegistrationId !== undefined) {
        await (0, _utils.changeActiveDelivery)({
          addressId: newAddressId,
          registrationId: newRegistrationId
        });
      }
      if (!addressFilters?.includes(_models.DeliveryOptionsFilter.MyAddresses)) {
        // mutateDeliveryOptions to get correct filters
        await mutateDeliveryOptions();
      }
      setActiveAddressFilter(_models.DeliveryOptionsFilter.MyAddresses);
    }
    if (typeof lastAddressId !== 'undefined' && prioritizedAddressId === lastAddressId) {
      (0, _logger2.logDebug)('Prioritized address ID is the same as last address ID. Mutating delivery options...', {
        prioritizedAddressId
      });
      void mutateDeliveryOptions();
    } else {
      // display last modified address item as 1st item of the suggestions list
      (0, _logger2.logDebug)('Setting prioritized address ID...', newAddressId);
      setPrioritizedAddressId(newAddressId);
    }
    // addressId should then be considered in next suggestions data query reload
    if (selectedItemId === newAddressId) {
      // refresh address data for item selected in main page
      setIsSelectedAddressEdited(true);
    }
    // If selected address was changed, remember it for case of closing dialog without select
  }

  // Solves successful create and edit address actions for logged-in user.
  // The address data manipulations are part of the address-form NPM library.
  function handleCallbackData(data) {
    (0, _logger2.logDebug)('handleCallbackData() called.\n\tCurrent form data:', data);
    void setAddressFormResults(data.addressId);
    if (handleBillingAddressRefresh) {
      handleBillingAddressRefresh();
    }
  }

  // Solves successful create and edit address actions for anonymous user.
  // The address data manipulations are part of the address-form NPM library.
  function handleCallbackRegistrationData(data) {
    (0, _logger2.logDebug)('handleCallbackRegistrationData() called.\n\tCurrent registration data:', data);
    const {
      addressId: usedAddressId,
      registrationId: usedRegistrationId
    } = data;
    void setAddressFormResults(usedAddressId, usedRegistrationId);
  }

  // Global API error handler for address form.
  // Validation errors are not solved by this, but rather internally in the form.
  function handleCallbackError(params) {
    const message = `Error while working with data for ${lastAddressId === undefined ? 'new address' : `address ID ${lastAddressId}`}`;
    let otherInfo = '';
    if (params instanceof Error) {
      otherInfo = params.message;
    } else {
      const {
        httpStatus,
        response
      } = params;
      if (httpStatus !== undefined) {
        otherInfo += `HTTP status code: ${httpStatus.toString()} `;
      }
      if (response !== undefined) {
        otherInfo += `Response: ${JSON.stringify(response)}`;
      }
    }
    const error = new Error(message);
    (0, _checkoutUtils.showErrorNotification)();
    logger.error(error, {
      response: otherInfo
    });
  }

  // The delete address action is not part of the address-form, so solved here.
  async function onConfirmDelete() {
    toggleConfirmDeleteAddressDialog();
    let success = false;
    if (typeof lastAddressId === 'undefined') {
      (0, _checkoutUtils.showErrorNotification)();
      const error = new Error('Last address id is not provided');
      logger.error(error);
      return success;
    }
    try {
      const response = await (0, _utils.deleteAddress)({
        addressId: lastAddressId,
        currentLanguage: language,
        customerId,
        formattedToken,
        host: globalApiUrl,
        tenant
      });
      (0, _logger2.logDebug)('Delete address response:', response);
      if ((0, _checkoutUtils.isErrorRestResponse)(response)) {
        const {
          message,
          Message
        } = response;
        throw new Error(message ?? Message);
      }
      (0, _logger2.logDebug)(`Address with ID ${lastAddressId} was successfully deleted. Current customer ID is ${customerId}.`);
      setLastAddressId(undefined);
      if (typeof prioritizedAddressId === 'undefined') {
        void mutateDeliveryOptions();
      } else {
        setPrioritizedAddressId(undefined);
      }
      // address was succesfully deleted, so we need to mutate suggestions
      success = true;
      // scroll to top of the shipping address section for flat selection UI
      if (isFlatSelection) {
        (0, _scrollToElement.scrollToElement)(addressSectionRef, isMobile);
      }
    } catch (error) {
      (0, _checkoutUtils.showErrorNotification)();
      logger.error(error, {
        action: `Deleting address with ID ${lastAddressId}`
      });
    }
    return success;
  }

  // This is set to true in the address-form props in 2 cases:
  // - anonymous user requests (1st) registration address (customerId === -1)
  // - pending registration user wants to edit registration address(es) (both customerId and registrationId are set)
  const useAnonymousCustomer = customerId === _auth.NOT_LOGGED_IN_USER_ID || Boolean(registrationId);
  // The customerId value provided to address-form has to be undefined when requesting the 1st registration address.
  const usedCustomerId = customerId === _auth.NOT_LOGGED_IN_USER_ID ? undefined : customerId;
  // If known, the registrationId should always be sent to the address-form, otherwise we create redundant
  // pending registration addresses / pending registration sessions.
  const usedRegistrationId = registrationId ?? undefined;
  // create address-form properties
  const addressFormTitle = formatMessage(lastAddressId === undefined ? 'addNewAlternativeAddress' : 'editAddress');
  const addressFormProps = {
    addressId: lastAddressId,
    appConfiguration: {
      globalApiUrl,
      identityUrl,
      processContext: _constants.processContext,
      staticApiUrl,
      tenant,
      currentLanguage: language,
      formattedToken,
      token
    },
    customerId: usedCustomerId,
    configForm: {
      // This header seems to be used only in dialog mode.
      header: addressFormTitle,
      hideActionButtons: false,
      isDialog: !isFlatSelection,
      isDialogOpen: isFlatSelection ? false : isAddressFormOpen,
      onCancel: () => {
        setIsAddressFormOpen(false);
        setWasLastAddressFormActionCancelled(true);
      },
      onCloseDialog: () => setIsAddressFormOpen(false)
    },
    handleCallbackError,
    registrationId: usedRegistrationId,
    useAnonymousCustomer
  };
  if (useAnonymousCustomer) {
    addressFormProps.handleCallbackRegistrationData = handleCallbackRegistrationData;
  } else {
    addressFormProps.handleCallbackData = handleCallbackData;
  }
  const confirmDeleteAddressDialogProps = {
    isDialogOpen: isConfirmDeleteAddressDialogOpen,
    onClose: toggleConfirmDeleteAddressDialog,
    onConfirmDelete
  };
  (0, _react.useEffect)(() => {
    if (isWaitingForNewAddressValidity && lastAddress.current && isLastAddressValid) {
      (0, _logger2.logDebug)('Selecting valid address.');
      onDeliveryItemActivation(lastAddress.current);
      setIsWaitingForNewAddressValidity(false);
    }
  }, [isLastAddressValid, isWaitingForNewAddressValidity, onDeliveryItemActivation]);
  return {
    addressFormProps,
    addressFormTitle,
    addressSectionRef,
    confirmDeleteAddressDialogProps,
    isAddressFormOpen,
    isConfirmDeleteAddressDialogOpen,
    lastAddress,
    lastAddressId,
    onCreateAddress,
    onDeleteAddress,
    onEditAddress,
    prioritizedAddressId,
    setIsAddressFormOpen,
    setIsLastAddressValid,
    setPrioritizedAddressId,
    wasLastAddressFormActionCancelled
  };
}